import { useState } from "react";
import { Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { EventFilterField, EventFilterTag, EventFilterOperator } from "@/hooks/useAdminEvents";
import { cn } from "@/lib/utils";

interface EventColumnFiltersProps {
  filterTags: EventFilterTag[];
  addFilter: (field: EventFilterField, value: string, operator?: EventFilterOperator) => void;
  removeFilter: (tagId: string) => void;
  clearAllFilters: () => void;
}

const FIELD_OPTIONS: {
  value: EventFilterField;
  label: string;
  placeholder: string;
  options?: string[];
  operators?: EventFilterOperator[];
}[] = [
  {
    value: "status",
    label: "Status",
    placeholder: "Select status",
    options: ["active", "archived", "canceled"],
  },
  { value: "title", label: "Title", placeholder: "Search by title...", operators: ["contains", "equals"] },
  { value: "venue", label: "Venue", placeholder: "Search by venue...", operators: ["contains", "equals"] },
  { value: "tag", label: "Tag", placeholder: "Search by tag...", operators: ["contains", "equals"] },
  { value: "host", label: "Host", placeholder: "Search by host...", operators: ["contains", "equals"] },
  { value: "price", label: "Price", placeholder: "Enter price...", operators: ["equals", ">=", "<=", ">", "<"] },
  { value: "capacity", label: "Capacity", placeholder: "Enter capacity...", operators: ["equals", ">=", "<=", ">", "<"] },
  { value: "booked", label: "Booked", placeholder: "Enter count...", operators: ["equals", ">=", "<=", ">", "<"] },
];

const FIELD_COLORS: Record<EventFilterField, string> = {
  status: "bg-slate-500/10 text-slate-700 border-slate-200",
  title: "bg-blue-500/10 text-blue-700 border-blue-200",
  venue: "bg-green-500/10 text-green-700 border-green-200",
  tag: "bg-purple-500/10 text-purple-700 border-purple-200",
  host: "bg-orange-500/10 text-orange-700 border-orange-200",
  price: "bg-amber-500/10 text-amber-700 border-amber-200",
  capacity: "bg-cyan-500/10 text-cyan-700 border-cyan-200",
  booked: "bg-pink-500/10 text-pink-700 border-pink-200",
};

const OPERATOR_LABELS: Record<EventFilterOperator, string> = {
  contains: "contains",
  equals: "=",
  ">=": "≥",
  "<=": "≤",
  ">": ">",
  "<": "<",
};

export const EventColumnFilters = ({
  filterTags,
  addFilter,
  removeFilter,
  clearAllFilters,
}: EventColumnFiltersProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<EventFilterField>("status");
  const [selectedOperator, setSelectedOperator] = useState<EventFilterOperator>("contains");
  const [inputValue, setInputValue] = useState("");

  const selectedFieldConfig = FIELD_OPTIONS.find((f) => f.value === selectedField);
  const hasOptions = !!selectedFieldConfig?.options;
  const hasOperators = !!selectedFieldConfig?.operators && selectedFieldConfig.operators.length > 0;

  const handleAddFilter = () => {
    if (inputValue.trim()) {
      const operator = hasOperators ? selectedOperator : undefined;
      addFilter(selectedField, inputValue.trim(), operator);
      setInputValue("");
      setIsOpen(false);
    }
  };

  const handleSelectOption = (value: string) => {
    addFilter(selectedField, value);
    setInputValue("");
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddFilter();
    }
  };

  const handleFieldChange = (field: EventFilterField) => {
    setSelectedField(field);
    setInputValue("");
    const fieldConfig = FIELD_OPTIONS.find((f) => f.value === field);
    if (fieldConfig?.operators?.length) {
      setSelectedOperator(fieldConfig.operators[0]);
    }
  };

  const formatTagDisplay = (tag: EventFilterTag) => {
    if (tag.operator) {
      return `${OPERATOR_LABELS[tag.operator]} ${tag.value}`;
    }
    return tag.value;
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1.5">
            <Filter className="h-3.5 w-3.5" />
            Filter
            {filterTags.length > 0 && (
              <span className="ml-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">
                {filterTags.length}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-3" align="end">
          <div className="space-y-3">
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Field</span>
              <div className="flex flex-wrap gap-1">
                {FIELD_OPTIONS.map((field) => (
                  <Button
                    key={field.value}
                    variant={selectedField === field.value ? "default" : "outline"}
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => handleFieldChange(field.value)}
                  >
                    {field.label}
                  </Button>
                ))}
              </div>
            </div>

            <Separator />

            {hasOperators && (
              <div className="space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Operator</span>
                <div className="flex flex-wrap gap-1">
                  {selectedFieldConfig?.operators?.map((op) => (
                    <Button
                      key={op}
                      variant={selectedOperator === op ? "default" : "outline"}
                      size="sm"
                      className="h-7 text-xs min-w-[40px]"
                      onClick={() => setSelectedOperator(op)}
                    >
                      {OPERATOR_LABELS[op]}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Value</span>
              {hasOptions ? (
                <div className="flex flex-wrap gap-1.5">
                  {selectedFieldConfig?.options?.map((opt) => (
                    <Button
                      key={opt}
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs capitalize"
                      onClick={() => handleSelectOption(opt)}
                    >
                      {opt}
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    placeholder={selectedFieldConfig?.placeholder}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="h-8 text-sm"
                    type={["price", "capacity", "booked"].includes(selectedField) ? "number" : "text"}
                  />
                  <Button size="sm" className="h-8" onClick={handleAddFilter}>
                    Add
                  </Button>
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {filterTags.map((tag) => (
        <div
          key={tag.id}
          className={cn(
            "flex items-center gap-1 h-7 px-2 rounded-md border text-xs font-medium",
            FIELD_COLORS[tag.field]
          )}
        >
          <span className="opacity-70">{tag.label}:</span>
          <span className="capitalize">{formatTagDisplay(tag)}</span>
          <button
            onClick={() => removeFilter(tag.id)}
            className="ml-0.5 hover:opacity-70 transition-opacity"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}

      {filterTags.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearAllFilters}
          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
        >
          Clear all
        </Button>
      )}
    </div>
  );
};
